import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SEO from "@/components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const claimSchema = z.object({
  name: z.string().min(2, "Минимум 2 символа").max(100),
  phone: z.string().min(10, "Введите корректный номер телефона").max(20),
  email: z.string().email("Введите корректный email"),
  product_name: z.string().min(2, "Выберите товар"),
  order_number: z.string().max(50).optional(),
  problem_description: z.string().min(10, "Опишите проблему подробнее (минимум 10 символов)").max(2000),
  preferred_pvz: z.string().min(5, "Укажите адрес ПВЗ для доставки замены").max(300),
});

const ClaimsPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, []);

  const form = useForm({
    resolver: zodResolver(claimSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      product_name: "",
      order_number: "",
      problem_description: "",
      preferred_pvz: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Read packer from cookie
      const packerMatch = document.cookie.match(/(?:^|;\s*)packer=([^;]*)/);
      const packer = packerMatch ? decodeURIComponent(packerMatch[1]) : null;
      const payload = packer ? { ...data, packer } : data;
      const response = await axios.post(`${API_URL}/api/claims`, payload);
      if (response.data.success) {
        setIsSuccess(true);
        toast.success("Обращение отправлено!", {
          description: response.data.message,
        });
        form.reset();
      }
    } catch (error) {
      toast.error("Ошибка", {
        description: error.response?.data?.detail || "Не удалось отправить обращение",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="claims-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-soft p-12 text-center max-w-md mx-4"
        >
          <div className="w-20 h-20 rounded-full bg-[#98FF98] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#1A1A1A]" />
          </div>
          <h2 className="heading-2 mb-4">Обращение принято!</h2>
          <p className="body-text mb-8">
            Мы получили вашу заявку и свяжемся с вами в ближайшее время для решения проблемы.
          </p>
          <Button
            onClick={() => setIsSuccess(false)}
            className="btn-primary"
            data-testid="claims-new-btn"
          >
            Отправить новое обращение
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="section-padding" data-testid="claims-page">
      <SEO
        title="Рекламация на детскую полку BE KIDS — Заявка на замену деталей"
        description="Оформите рекламацию на детскую полку BE KIDS. Бесплатная замена деталей с производственным браком в течение 12 месяцев. Быстрое рассмотрение заявки."
        path="/claims"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-[#FFDAB9] flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-[#1A1A1A]" />
          </div>
          <h1 className="heading-1 mb-4" data-testid="claims-title">
            Обращение по рекламации
          </h1>
          <p className="body-text max-w-xl mx-auto" data-testid="claims-description">
            Если у вас возникли проблемы с товаром, заполните форму ниже. 
            Мы рассмотрим ваше обращение и организуем замену бракованных деталей.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-soft p-8 md:p-12 bg-[#FFDAB9]/10"
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="claims-form">
              {/* Contact Info */}
              <div className="space-y-6">
                <h3 className="heading-3 text-lg border-b border-[#1A1A1A]/10 pb-3">
                  Контактные данные
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#1A1A1A] font-medium">Ваше имя *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Иван Иванов"
                            className="input-custom"
                            data-testid="claims-name-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#1A1A1A] font-medium">Телефон *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="+7 (999) 123-45-67"
                            className="input-custom"
                            data-testid="claims-phone-input"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#1A1A1A] font-medium">Email *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="ivan@example.com"
                          className="input-custom"
                          data-testid="claims-email-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Order Info */}
              <div className="space-y-6 pt-6">
                <h3 className="heading-3 text-lg border-b border-[#1A1A1A]/10 pb-3">
                  Информация о заказе
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="product_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#1A1A1A] font-medium">Товар *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="input-custom" data-testid="claims-product-select">
                              <SelectValue placeholder="Выберите товар" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {products.map((product) => (
                              <SelectItem
                                key={product.id}
                                value={`${product.name} - ${product.size}`}
                                data-testid={`claims-product-option-${product.id}`}
                              >
                                {product.name} - {product.size}
                              </SelectItem>
                            ))}
                            <SelectItem value="Другой товар">Другой товар</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="order_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#1A1A1A] font-medium">Номер заказа</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Номер с маркетплейса"
                            className="input-custom"
                            data-testid="claims-order-input"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-[#808080]">
                          Необязательно, но ускорит обработку
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Problem Description */}
              <div className="space-y-6 pt-6">
                <h3 className="heading-3 text-lg border-b border-[#1A1A1A]/10 pb-3">
                  Описание проблемы
                </h3>

                <FormField
                  control={form.control}
                  name="problem_description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#1A1A1A] font-medium">Опишите проблему *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Подробно опишите, что именно не так с товаром: дефекты, повреждения, некомплект и т.д."
                          className="input-custom min-h-[150px] resize-none"
                          data-testid="claims-problem-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="preferred_pvz"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[#1A1A1A] font-medium">
                        Удобный ПВЗ для доставки замены *
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Укажите адрес пункта выдачи заказов (Wildberries/Ozon), куда удобно доставить замену"
                          className="input-custom min-h-[80px] resize-none"
                          data-testid="claims-pvz-input"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs text-[#808080]">
                        Укажите город, улицу и название/адрес ПВЗ
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={isSubmitting}
                data-testid="claims-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Отправка...
                  </>
                ) : (
                  "Отправить обращение"
                )}
              </Button>

              <p className="text-xs text-center text-[#808080]">
                Нажимая кнопку, вы соглашаетесь на обработку персональных данных
              </p>
            </form>
          </Form>
        </motion.div>
      </div>
    </div>
  );
};

export default ClaimsPage;
